// ClientDashboard.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import Select, { SingleValue } from "react-select";
import { ThreeCircles } from "react-loader-spinner";
import {
  CheckCircle,
  HourglassSplit,
  XCircle,
  FileText,
  InfoCircle,
  PeopleFill,
  GraphUpArrow,
} from "react-bootstrap-icons";

import styles from "./ClientDashboard.module.css";
import { fetchDashboardData } from "./ClientDashboard.service";

import {
  ALL_OPTION,
  SelectOption,
  DashboardFilters,
  buildFiltersPayload,
  getCurrentTaxYearValue,
  ApprovalStatusRow,
  OfficeStatusRow,
  PartnerStatusRow,
  ClientPracticeSummaryRow,
  LineOfServiceSummaryRow,
  ClientTypeSummaryRow,
  PrimaryPartnerClientTypeRow,
  OfficeClientTypeRow,
  buildClientOfficeStackedColumnOptions,
  buildClientPartnerStackedBarOptions,
  buildClientsInPracticeLineOfServicePieOptions,
  buildClientsInPracticeClientTypeComboOptions,
  buildClientsInPracticePrimaryPartnerStackedBarOptions,
  buildClientsInPracticeOfficeStackedColumnOptions,
  formatCompactCurrency,
} from "./ClientDashboard.utils";

import { useGlobalStore } from "../../store/globalStore";
import { PurposeOption } from "../../client_onboarding/ClientsGrid/ClientsGrid.utils";

type ClientDashboardProps = {
  transporter?: (pill: string, options?: any) => void;
  saveParamToApi?: (parameter_name: string, value: any) => Promise<void>;
  canViewExecutive?: boolean;
};

const ClientDashboard: React.FC<ClientDashboardProps> = ({
  transporter,
  saveParamToApi,
  canViewExecutive = false,
}) => {
  // Base backend URL used by dashboard API calls
  const baseUri = process.env.REACT_APP_BASE_URI || "";

  // Dropdown options coming from global store
  const purposeOptionsFromStore: PurposeOption[] = useGlobalStore((s) => s.purposeOptions);
  const officeOptionsFromStore: Array<{ label: string; value: string }> = useGlobalStore(
    (s) => s.officeLocationOptions
  );
  const partnerOptionsFromStore: Array<{ label: string; value: string }> = useGlobalStore(
    (s) => s.primaryPartnerOptions
  );

  // Helper to convert store dropdown items into react-select format
  // and always keep "All" as the first option
  const toSelectOptions = (items: any[]): SelectOption[] => {
    const mapped = items.map((x) => ({ label: x.label, value: x.value }));
    return [ALL_OPTION, ...mapped.filter((o) => o.value !== "All")];
  };

  // Memoized LOS dropdown options
  const lineOfServiceOptions = useMemo(
    () => toSelectOptions(purposeOptionsFromStore),
    [purposeOptionsFromStore]
  );

  // Memoized office dropdown options
  const officeOptions = useMemo(
    () => toSelectOptions(officeOptionsFromStore),
    [officeOptionsFromStore]
  );

  // FY dropdown currently supports All + current FY
  const fyOptions: SelectOption[] = useMemo(() => {
    const current = getCurrentTaxYearValue();
    return [ALL_OPTION, { label: current, value: current }];
  }, []);

  // Finds matching dropdown option object from a raw value
  // and falls back to All if not found
  const findOption = (options: SelectOption[], value?: string | null): SelectOption => {
    if (!value) return ALL_OPTION;
    return options.find((o) => o.value === value) || { label: value, value };
  };

  // Selected dashboard filters
  const [selectedLOS, setSelectedLOS] = useState<SelectOption>(ALL_OPTION);
  const [selectedOffice, setSelectedOffice] = useState<SelectOption>(ALL_OPTION);
  const [selectedPartner, setSelectedPartner] = useState<SelectOption>(ALL_OPTION);
  const [selectedFY, setSelectedFY] = useState<SelectOption>(ALL_OPTION);
  const [filtersReady, setFiltersReady] = useState(false);

  // Executive view data
  const [summaryRows, setSummaryRows] = useState<ClientPracticeSummaryRow[]>([]);
  const [lineOfServiceRows, setLineOfServiceRows] = useState<LineOfServiceSummaryRow[]>([]);
  const [clientTypeRows, setClientTypeRows] = useState<ClientTypeSummaryRow[]>([]);
  const [primaryPartnerClientTypeRows, setPrimaryPartnerClientTypeRows] = useState<PrimaryPartnerClientTypeRow[]>([]);
  const [officeClientTypeRows, setOfficeClientTypeRows] = useState<OfficeClientTypeRow[]>([]);

  // Dashboard view toggle state
  // Filters stay common for both views, only KPI/chart section changes
  const [dashboardView, setDashboardView] = useState<"Operations" | "Executive">("Operations");

  // Partner dropdown depends on selected office
  // If office is All, show all partners
  // Otherwise, show only partners belonging to the selected office
  const partnerOptions = useMemo(() => {
    const selectedOfficeValue = selectedOffice?.value;

    if (!selectedOfficeValue || selectedOfficeValue === "All") {
      return toSelectOptions(partnerOptionsFromStore);
    }

    const filteredPartners = partnerOptionsFromStore.filter((partner: any) => {
      const partnerOffice =
        partner.location ||
        partner.office ||
        partner.Office ||
        partner.officeLocation ||
        "";

      return String(partnerOffice).trim() === String(selectedOfficeValue).trim();
    });

    return [ALL_OPTION, ...filteredPartners.map((x) => ({ label: x.label, value: x.value }))];
  }, [partnerOptionsFromStore, selectedOffice]);

  // Set initial dashboard defaults on first load / whenever FY options refresh
  // Current behavior:
  // - LOS = All
  // - Office = All
  // - Partner = All
  // - FY = current tax year
  useEffect(() => {
    setSelectedLOS(ALL_OPTION);
    setSelectedOffice(ALL_OPTION);
    setSelectedPartner(ALL_OPTION);

    const fyDefault = findOption(fyOptions, getCurrentTaxYearValue());
    setSelectedFY(fyDefault);

    setFiltersReady(true);
  }, []);

  // If office changes and current selected partner no longer belongs to that office,
  // reset partner back to All
  useEffect(() => {
    const selectedOfficeValue = selectedOffice?.value;

    if (!selectedOfficeValue || selectedOfficeValue === "All") {
      return;
    }

    const partnerStillValid = partnerOptions.some(
      (option) => option.value === selectedPartner?.value
    );

    if (!partnerStillValid) {
      setSelectedPartner(ALL_OPTION);
    }
  }, [selectedOffice, partnerOptions, selectedPartner]);

  // Convert selected dropdown values into API filter format
  // Only send arrays when a real value is selected; omit when All
  const filtersForApi: DashboardFilters = useMemo(() => {
    const toArrayOrUndefined = (opt: SelectOption): string[] | undefined =>
      opt?.value && opt.value !== "All" ? [opt.value] : undefined;

    return {
      businessUnits: toArrayOrUndefined(selectedLOS),
      offices: toArrayOrUndefined(selectedOffice),
      partners: toArrayOrUndefined(selectedPartner),
      fiscalYears: toArrayOrUndefined(selectedFY),
    };
  }, [selectedLOS, selectedOffice, selectedPartner, selectedFY]);

  // Final payload passed to backend
  const payload = useMemo(() => {
    return buildFiltersPayload(filtersForApi);
  }, [filtersForApi]);

  // Dashboard loading state + chart data state
  const [loading, setLoading] = useState(true);
  const [navigating, setNavigating] = useState(false);
  const latestRequestRef = useRef(0);
  const [approvalRows, setApprovalRows] = useState<ApprovalStatusRow[]>([]);
  const [officeRows, setOfficeRows] = useState<OfficeStatusRow[]>([]);
  const [partnerRows, setPartnerRows] = useState<PartnerStatusRow[]>([]);

  // Fetch dashboard data whenever filters change
    // Fetch dashboard data whenever filters change
  useEffect(() => {
    if (!filtersReady) return;
    const run = async () => {
      const requestId = ++latestRequestRef.current;
      setLoading(true);

      try {
        const res = await fetchDashboardData(baseUri, payload);

        if (requestId !== latestRequestRef.current) return;

        // If every section is empty, clear all charts/state
        const noData =
          res.approvalStatus.length === 0 &&
          res.office.length === 0 &&
          res.primaryPartner.length === 0 &&
          res.summary.length === 0 &&
          res.lineOfService.length === 0 &&
          res.clientType.length === 0 &&
          res.primaryPartnerClientType.length === 0 &&
          res.officeClientType.length === 0;

        if (noData) {
          setApprovalRows([]);
          setOfficeRows([]);
          setPartnerRows([]);
          setSummaryRows([]);
          setLineOfServiceRows([]);
          setClientTypeRows([]);
          setPrimaryPartnerClientTypeRows([]);
          setOfficeClientTypeRows([]);

          await new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve());
          });

          if (requestId === latestRequestRef.current) {
            setLoading(false);
          }
          return;
        }

        // Normalize approval chart / KPI rows
        setApprovalRows(
          res.approvalStatus.map((r: any) => ({
            ApprovalStatus: r.ApprovalStatus,
            TotalCount: r.TotalCount ?? r.COUNT ?? 0,
          }))
        );

        // Normalize office chart rows
        setOfficeRows(
          res.office.map((r: any) => ({
            Office: r.Office,
            ApprovalStatus: r.ApprovalStatus,
            TotalCount: r.TotalCount ?? r.COUNT ?? 0,
          }))
        );

        // Normalize partner chart rows
        setPartnerRows(
          res.primaryPartner.map((r: any) => ({
            PrimaryPartner: r.PrimaryPartner,
            ApprovalStatus: r.ApprovalStatus,
            TotalCount: r.TotalCount ?? r.COUNT ?? 0,
          }))
        );

        // Normalize executive summary KPI row
        setSummaryRows(
          (res.summary || []).map((r: any) => ({
            TotalClientsInPractice: r.TotalClientsInPractice ?? r.ClientCount ?? r.TotalCount ?? 0,
            TotalForecastAmount: r.TotalForecastAmount ?? r.ForecastAmount ?? r.TotalAmount ?? 0,
          }))
        );

        // Normalize LOS executive chart rows
        setLineOfServiceRows(
          (res.lineOfService || []).map((r: any) => ({
            LineOfService: r.LineOfService,
            ClientCount: r.ClientCount ?? r.TotalCount ?? r.COUNT ?? 0,
            ForecastAmount: r.ForecastAmount ?? r.TotalAmount ?? 0,
          }))
        );

        // Normalize client type executive chart rows
        setClientTypeRows(
          (res.clientType || []).map((r: any) => ({
            ClientType: r.ClientType,
            ClientCount: r.ClientCount ?? r.TotalCount ?? r.COUNT ?? 0,
            ForecastAmount: r.ForecastAmount ?? r.TotalAmount ?? 0,
          }))
        );

        // Normalize primary partner x client type rows
        setPrimaryPartnerClientTypeRows(
          (res.primaryPartnerClientType || []).map((r: any) => ({
            PrimaryPartner: r.PrimaryPartner,
            ClientType: r.ClientType,
            ClientCount: r.ClientCount ?? r.TotalCount ?? r.COUNT ?? 0,
            ForecastAmount: r.ForecastAmount ?? r.TotalAmount ?? 0,
            TotalPrimaryPartnerCount: r.TotalPrimaryPartnerCount ?? 0,
            TotalPrimaryPartnerAmount: r.TotalPrimaryPartnerAmount ?? 0,
          }))
        );

        // Normalize office x client type rows
        setOfficeClientTypeRows(
          (res.officeClientType || []).map((r: any) => ({
            Office: r.Office,
            ClientType: r.ClientType,
            ClientCount: r.ClientCount ?? r.TotalCount ?? r.COUNT ?? 0,
            ForecastAmount: r.ForecastAmount ?? r.TotalAmount ?? 0,
            TotalOfficeCount: r.TotalOfficeCount ?? 0,
            TotalOfficeAmount: r.TotalOfficeAmount ?? 0,
          }))
        );

        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve());
        });

        if (requestId === latestRequestRef.current) {
          setLoading(false);
        }
      } catch (e) {
        if (requestId !== latestRequestRef.current) return;

        console.error(e);
        setApprovalRows([]);
        setOfficeRows([]);
        setPartnerRows([]);
        setSummaryRows([]);
        setLineOfServiceRows([]);
        setClientTypeRows([]);
        setPrimaryPartnerClientTypeRows([]);
        setOfficeClientTypeRows([]);

        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve());
        });

        if (requestId === latestRequestRef.current) {
          setLoading(false);
        }
      }
    };

    run();
  }, [baseUri, payload, selectedLOS.value, selectedOffice.value, selectedPartner.value, selectedFY.value,]);

  // Pull theme colors from CSS variables and chart-theme local storage
  // Used mainly for chart color consistency
  const theme = useMemo(() => {
    const root = document.documentElement;
    const css = getComputedStyle(root);

    let palette: string[] = [];
    try {
      const rawTheme = localStorage.getItem("chart-theme");
      const parsedTheme = rawTheme ? JSON.parse(rawTheme) : null;
      palette = Array.isArray(parsedTheme?.colors) ? parsedTheme.colors : [];
    } catch (e) {
      palette = [];
    }

    return {
      primary: css.getPropertyValue("--theme1_primary").trim() || "#1976D2",
      tertiary: css.getPropertyValue("--theme1_tertiary").trim() || "#E3F2FD",
      palette,
    };
  }, []);

  // Build final Highcharts options for office and partner charts
  const officeChartOptions = useMemo(
    () => buildClientOfficeStackedColumnOptions(officeRows),
    [officeRows]
  );

  const partnerChartOptions = useMemo(
    () => buildClientPartnerStackedBarOptions(partnerRows),
    [partnerRows]
  );

    const executiveLosPieOptions = useMemo(
    () => buildClientsInPracticeLineOfServicePieOptions(lineOfServiceRows, theme),
    [lineOfServiceRows, theme]
  );

  const executiveClientTypeComboOptions = useMemo(
    () => buildClientsInPracticeClientTypeComboOptions(clientTypeRows, theme),
    [clientTypeRows, theme]
  );

  const executivePartnerOptions = useMemo(
    () =>
      buildClientsInPracticePrimaryPartnerStackedBarOptions(
        primaryPartnerClientTypeRows,
        theme
      ),
    [primaryPartnerClientTypeRows, theme]
  );

  const executiveOfficeOptions = useMemo(
    () =>
      buildClientsInPracticeOfficeStackedColumnOptions(
        officeClientTypeRows,
        theme
      ),
    [officeClientTypeRows, theme]
  );

  // Helper to get KPI count for a given approval status
  const getApprovalCount = (statusName: string) =>
    approvalRows.find(
      (r) =>
        String(r.ApprovalStatus || "").trim().toLowerCase() === statusName.trim().toLowerCase()
    )?.TotalCount ?? 0;

  // Operations KPI values
  const approvedCount = getApprovalCount("Approved");
  const pendingApprovalCount = getApprovalCount("Pending Approval");
  const rejectedCount = getApprovalCount("Rejected");
  const prospectCount = getApprovalCount("Prospect");

  // Executive KPI values
  const totalClientsInPractice = useMemo(() => {
    return summaryRows.reduce(
      (sum, row) => sum + Number(row.TotalClientsInPractice ?? 0),
      0
    );
  }, [summaryRows]);

  const totalForecastAmount = useMemo(() => {
    return summaryRows.reduce(
      (sum, row) => sum + Number(row.TotalForecastAmount ?? 0),
      0
    );
  }, [summaryRows]);

  // Build saved grid filter payload for Clients page when KPI is clicked
  // This is used to navigate user to Clients grid with matching filters already applied
  const buildKpiGridPayload = (
  kpiType: "approved" | "pendingApproval" | "rejected" | "prospect"
) => {
  const gridModel: Record<string, any> = {};
  const serverFilter: Record<string, any> = {};

  // hard reset all known dashboard-driven filters first
  gridModel.approvalStatus = null;
  gridModel.primaryPartner = null;
  gridModel.officeLocation = null;
  gridModel.lineOfService = null;
  gridModel.createdDate = null;

  // include dashboard dropdown filters
  if (selectedLOS?.value && selectedLOS.value !== "All") {
    gridModel.lineOfService = {
      values: [selectedLOS.value],
      filterType: "set",
    };
    serverFilter.LineOfService = [selectedLOS.value];
  }

  if (selectedOffice?.value && selectedOffice.value !== "All") {
    gridModel.officeLocation = {
      values: [selectedOffice.value],
      filterType: "set",
    };
    serverFilter.OfficeLocation = [selectedOffice.value];
  }

  if (selectedPartner?.value && selectedPartner.value !== "All") {
    gridModel.primaryPartner = {
      values: [selectedPartner.value],
      filterType: "set",
    };
    serverFilter.PrimaryPartner = [selectedPartner.value];
  }

  if (selectedFY?.value && selectedFY.value !== "All") {
    gridModel.createdDate = {
      type: "contains",
      filter: selectedFY.value.replace("FY", ""),
      filterType: "text",
    };
    serverFilter.CreatedDate = [selectedFY.value.replace("FY", "")];
  }

  if (kpiType === "approved") {
    gridModel.approvalStatus = {
      values: ["Approved"],
      filterType: "set",
    };
    serverFilter.Status = ["Approved"];
  }

  if (kpiType === "pendingApproval") {
    gridModel.approvalStatus = {
      values: ["Pending Approval"],
      filterType: "set",
    };
    serverFilter.Status = ["Pending Approval"];
  }

  if (kpiType === "rejected") {
    gridModel.approvalStatus = {
      values: ["Rejected"],
      filterType: "set",
    };
    serverFilter.Status = ["Rejected"];
  }

  if (kpiType === "prospect") {
    gridModel.approvalStatus = {
      values: ["Prospect"],
      filterType: "set",
    };
    serverFilter.Status = ["Prospect"];
  }

  return {
    gridKey: "ClientsGrid.prospects",
    savedAt: new Date().toISOString(),
    viewMode: "myView",
    gridModel,
    serverFilter,
  };
};

  // On KPI click:
  // 1. Build payload
  // 2. Save it as personalization filter for the user
  // 3. Navigate to Clients page

  const buildClientDashboardNavUrl = (payload: any) => {
    const encodedPayload = encodeURIComponent(btoa(JSON.stringify(payload)));
    return `${window.location.origin}/dashboard?tab=clients&targetTab=prospect&kpiPayload=${encodedPayload}`;
  };

  const getKpiHref = (
    kpiType: "approved" | "pendingApproval" | "rejected" | "prospect"
  ) => {
    return buildClientDashboardNavUrl(buildKpiGridPayload(kpiType));
  };

  const handleKpiClick = (
    kpiType: "approved" | "pendingApproval" | "rejected" | "prospect",
    event?: React.MouseEvent
  ) => {
    const targetUrl = getKpiHref(kpiType);

    const isNewTabAction =
      !!event && (event.button === 1 || event.metaKey || event.ctrlKey);

    if (isNewTabAction) {
      window.open(targetUrl, "_blank", "noopener,noreferrer");
      return;
    }

    setNavigating(true);
    window.location.href = targetUrl;
  };

  return (
    <>
      {/* Full page loader while dashboard API calls are in progress */}
      {(loading || navigating) && (
        <div className={styles.creatorLoader}>
          <ThreeCircles
            height="50"
            width="50"
            color="#4fa94d"
            visible={true}
            ariaLabel="three-circles-rotating"
            outerCircleColor="var(--theme1_tertiary)"
            innerCircleColor="var(--theme1_secondary)"
            middleCircleColor="var(--theme1_primary)"
          />
          <div className={styles.loaderText}>
            {navigating ? "Taking to Client Page" : "Loading dashboard data"}
          </div>
        </div>
      )}

      <div className={styles.page}>
        {/* Dashboard title row */}
        <Row className={styles.headerRow}>
          <Col md={6}>
            <p className={styles.title}>Client Dashboard</p>
          </Col>

          {canViewExecutive && (
          <Col md={6} className={styles.toggleCol}>
            <div className={styles.dashboardToggle}>
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="toggle-tooltip-operations" className={styles.toggleTooltip}>
                    Shows insights for prospects
                  </Tooltip>
                }
              >
                <span>
                  <button
                    type="button"
                    className={`${styles.toggleBtn} ${
                      dashboardView === "Operations" ? styles.activeToggle : ""
                    }`}
                    onClick={() => setDashboardView("Operations")}
                  >
                    Operations
                  </button>
                </span>
              </OverlayTrigger>

              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="toggle-tooltip-executive" className={styles.toggleTooltip}>
                    Shows insights for clients in practice
                  </Tooltip>
                }
              >
                <span>
                  <button
                    type="button"
                    className={`${styles.toggleBtn} ${
                      dashboardView === "Executive" ? styles.activeToggle : ""
                    }`}
                    onClick={() => setDashboardView("Executive")}
                  >
                    Executive
                  </button>
                </span>
              </OverlayTrigger>
            </div>
          </Col>
          )}
        </Row>

        <>
          {/* Dashboard filter row */}
          <Row className={`${styles.filterRow} mb-4`}>
            <Col>
              <label className={styles.filterLabel}>Line of Service</label>
              <Select
                value={selectedLOS}
                onChange={(e: SingleValue<SelectOption>) => e && setSelectedLOS(e)}
                options={lineOfServiceOptions}
                classNamePrefix="react-select"
              />
            </Col>

            <Col>
              <label className={styles.filterLabel}>Office</label>
              <Select
                value={selectedOffice}
                onChange={(e: SingleValue<SelectOption>) => e && setSelectedOffice(e)}
                options={officeOptions}
                classNamePrefix="react-select"
              />
            </Col>

            <Col>
              <label className={styles.filterLabel}>Partner</label>
              <Select
                value={selectedPartner}
                onChange={(e: SingleValue<SelectOption>) => e && setSelectedPartner(e)}
                options={partnerOptions}
                classNamePrefix="react-select"
              />
            </Col>

            <Col>
              <label className={styles.filterLabel}>FY</label>
              <Select
                value={selectedFY}
                onChange={(e: SingleValue<SelectOption>) => e && setSelectedFY(e)}
                options={fyOptions}
                classNamePrefix="react-select"
              />
            </Col>
          </Row>

          {/* KPI banner row */}
          {dashboardView === "Operations" ? (
            <Row className={`${styles.kpiBanner} mb-3`}>
              <Col
                className={`${styles.kpiCol} ${styles.clickableKpi} d-flex align-items-center justify-content-center`}
              >
              <a
                href={getKpiHref("prospect")}
                className={styles.kpiLink}
                onClick={() => setNavigating(true)}
              >
                <FileText size={40} className={styles.kpiIcon} />
                <div className={styles.kpiTextBlock}>
                  <div className={styles.kpiLabelRow}>
                    <small className={styles.kpiSubLarge}>Prospect Clients</small>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="kpi-tooltip-prospect" className={styles.kpiTooltip}>
                          Go to Clients with this filter
                        </Tooltip>
                      }
                    >
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <InfoCircle size={14} className={styles.kpiInfoIcon} />
                      </span>
                    </OverlayTrigger>
                  </div>
                  <h4 className={styles.kpiValue}>{prospectCount}</h4>
                </div>
                </a>
              </Col>

              <Col
                className={`${styles.kpiColRight} ${styles.clickableKpi} d-flex align-items-center justify-content-center mb-3 mb-md-0`}
              >
              <a
                href={getKpiHref("pendingApproval")}
                className={styles.kpiLink}
                onClick={() => setNavigating(true)}
              >
                <HourglassSplit size={46} className={styles.kpiIcon} style={{ marginRight: "2px" }}/>      
                <div className={styles.kpiTextBlock}>
                  <div className={styles.kpiLabelRow}>
                    <small className={styles.kpiSubLarge}>Pending Approvals</small>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="kpi-tooltip-pending" className={styles.kpiTooltip}>
                          Go to Clients with this filter
                        </Tooltip>
                      }
                    >
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <InfoCircle size={14} className={styles.kpiInfoIcon} />
                      </span>
                    </OverlayTrigger>
                  </div>
                  <h4 className={styles.kpiValue}>{pendingApprovalCount}</h4>
                </div>
                </a>
              </Col>

              <Col
                className={`${styles.kpiColRight} ${styles.clickableKpi} d-flex align-items-center justify-content-center mb-3 mb-md-0`}
              >
              <a
                href={getKpiHref("rejected")}
                className={styles.kpiLink}
                onClick={() => setNavigating(true)}
              >
                <XCircle size={46} className={styles.kpiIcon} style={{ marginRight: "2px" }} />
                <div className={styles.kpiTextBlock}>
                  <div className={styles.kpiLabelRow}>
                    <small className={styles.kpiSubLarge}>Rejected Prospects</small>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="kpi-tooltip-rejected" className={styles.kpiTooltip}>
                          Go to Clients with this filter
                        </Tooltip>
                      }
                    >
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <InfoCircle size={14} className={styles.kpiInfoIcon} />
                      </span>
                    </OverlayTrigger>
                  </div>
                  <h4 className={styles.kpiValue}>{rejectedCount}</h4>
                </div>
                </a>
              </Col>

              <Col
                className={`${styles.kpiColRight} ${styles.clickableKpi} d-flex align-items-center justify-content-center mb-3 mb-md-0`}
              >
              <a
                href={getKpiHref("approved")}
                className={styles.kpiLink}
                onClick={() => setNavigating(true)}
              >
                <CheckCircle size={40} className={styles.kpiIcon} style={{ marginRight: "2px" }} />
                <div className={styles.kpiTextBlock}>
                  <div className={styles.kpiLabelRow}>
                    <small className={styles.kpiSubLarge}>Approved Prospects</small>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="kpi-tooltip-approved" className={styles.kpiTooltip}>
                          Go to Clients with this filter
                        </Tooltip>
                      }
                    >
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <InfoCircle size={14} className={styles.kpiInfoIcon} />
                      </span>
                    </OverlayTrigger>
                  </div>
                  <h4 className={styles.kpiValue}>{approvedCount}</h4>
                </div>
                </a>
              </Col>
            </Row>
          ) : (
            <Row className={`${styles.kpiBanner} mb-3`}>
              <Col
                className={`${styles.kpiCol} d-flex align-items-center justify-content-center mb-3 mb-md-0`}
              >
                <PeopleFill size={40} className={styles.kpiIcon} style={{ marginRight: "18px" }}/>
                <div className={styles.kpiTextBlock}>
                  <div className={styles.kpiLabelRow}>
                    <small className={styles.kpiSubLarge}>Total Clients in Practice</small>
                  </div>
                  <h4 className={styles.kpiValue}>{Number(totalClientsInPractice ?? 0).toLocaleString()}</h4>
                </div>
              </Col>

              <Col
                className={`${styles.kpiColRight} d-flex align-items-center justify-content-center mb-3 mb-md-0`}
              >
                <GraphUpArrow size={40} className={styles.kpiIcon} style={{ marginRight: "18px" }} />
                <div className={styles.kpiTextBlock}>
                  <div className={styles.kpiLabelRow}>
                    <small className={styles.kpiSubLarge}>Total Expected Revenue</small>
                  </div>
                  <h4 className={styles.kpiValue}>${formatCompactCurrency(totalForecastAmount)}</h4>
                </div>
              </Col>
            </Row>
          )}

          {/* Chart section */}
          {dashboardView === "Operations" ? (
            <Row className="justify-content-center text-center gx-3">
              <Col md={6} className="mb-3 d-flex justify-content-center">
                <div className={styles.chartCard}>
                  <HighchartsReact
                    key="client-partner"
                    highcharts={Highcharts}
                    options={partnerChartOptions}
                    immutable={true}
                  />
                </div>
              </Col>

              <Col md={6} className="mb-3 d-flex justify-content-center">
                <div className={styles.chartCard}>
                  <HighchartsReact
                    key="client-office"
                    highcharts={Highcharts}
                    options={officeChartOptions}
                    immutable={true}
                  />
                </div>
              </Col>
            </Row>
          ) : (
            <>
              <Row className="justify-content-center text-center gx-3">
                <Col md={6} className="mb-3 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact
                      key="client-executive-los"
                      highcharts={Highcharts}
                      options={executiveLosPieOptions}
                      immutable={true}
                    />
                  </div>
                </Col>

                <Col md={6} className="mb-3 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact
                      key="client-executive-client-type"
                      highcharts={Highcharts}
                      options={executiveClientTypeComboOptions}
                      immutable={true}
                    />
                  </div>
                </Col>
              </Row>

              <Row className="justify-content-center text-center gx-3">
                <Col md={6} className="mb-3 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact
                      key="client-executive-office"
                      highcharts={Highcharts}
                      options={executiveOfficeOptions}
                      immutable={true}
                    />
                  </div>
                </Col>
                
                <Col md={6} className="mb-3 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact
                      key="client-executive-partner"
                      highcharts={Highcharts}
                      options={executivePartnerOptions}
                      immutable={true}
                    />
                  </div>
                </Col>

              </Row>
            </>
          )}
        </>
      </div>
    </>
  );
};

export default ClientDashboard;